import React, { useRef, useEffect, useCallback } from 'react';
import MessageBubble from './MessageBubble';
import type { ExpertMessage, StreamingMessage } from '../../types/expert';
import type { UseTTSReturn } from '../../hooks/useTTS';

interface MessageListProps {
  messages: ExpertMessage[];
  streamingMessage: StreamingMessage | null;
  tts?: UseTTSReturn;
}

// If the user is within this many px of the bottom, treat as "at bottom"
const SCROLL_THRESHOLD = 100;

const MessageList: React.FC<MessageListProps> = ({ messages, streamingMessage, tts }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  // Starts true so the initial load scrolls to the bottom
  const isNearBottom = useRef(true);
  const prevMsgCount = useRef(messages.length);

  const scrollToBottom = useCallback((smooth = true) => {
    bottomRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'instant' });
  }, []);

  // Update isNearBottom whenever the user scrolls
  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    isNearBottom.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD;
  }, []);

  // New message committed to the list
  useEffect(() => {
    const newCount = messages.length;
    if (newCount > prevMsgCount.current) {
      const lastMsg = messages[newCount - 1];
      // When the user sends a message, always snap to bottom so they see the reply
      if (lastMsg?.role === 'user') {
        isNearBottom.current = true;
      }
      prevMsgCount.current = newCount;
    }
    if (isNearBottom.current) {
      scrollToBottom();
    }
  }, [messages, scrollToBottom]);

  // Streaming tokens: follow only if already near the bottom
  useEffect(() => {
    if (streamingMessage && isNearBottom.current) {
      // Use instant scroll during streaming to avoid animation lag
      scrollToBottom(false);
    }
  }, [streamingMessage?.content, scrollToBottom]);

  if (messages.length === 0 && !streamingMessage) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center">
          <div className="w-12 h-12 bg-teal-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
            <span className="text-teal-400 text-xl">?</span>
          </div>
          <p className="text-slate-400 text-sm">Ask the Expert anything about markets, trades, or your portfolio.</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto p-4">
      {messages.map(msg => (
        <MessageBubble key={msg.id} message={msg} tts={tts} />
      ))}
      {streamingMessage && (
        <MessageBubble message={streamingMessage} tts={tts} />
      )}
      <div ref={bottomRef} />
    </div>
  );
};

export default MessageList;
